<template>
  <div class="w-full h-full bg-white flex flex-col px-8 overflow-y-auto">
    <div class="flex items-center justify-between py-8">
      <h2 class="font-semibold">Indexation Overview</h2>
    </div>

    <div class="flex gap-4 mb-6">
      <div class="flex flex-col gap-1 w-64">
        <label class="font-medium">Organization ID</label>
        <base-input v-model.trim="filters.orgId"
                    name="ao-org-filter"
                    variant="editor"
                    class="border-gray-border h-10"
                    placeholder="filter by organization id"
                    @blur="reload"
                    @keyup.enter.native="reload"
        />
      </div>
      <div class="flex flex-col gap-1 w-48">
        <label class="font-medium">Status</label>
        <base-select :value="filters.status" :options="statusFilterOptions" class="h-10" @input="onStatusChange"/>
      </div>
    </div>

    <table class="w-full text-sm mb-8">
      <thead>
        <tr class="text-left text-gray-dark border-solid border-0 border-b border-gray-border">
          <th class="py-2">Organization</th>
          <th class="py-2">Index</th>
          <th class="py-2">Status</th>
          <th class="py-2">Products</th>
          <th class="py-2">Started / Duration</th>
          <th class="py-2">Error</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="execution in sortedExecutions">
          <tr :key="execution.id" class="border-solid border-0 border-b border-gray-border">
            <td class="py-2">{{ execution.organization_name || execution.organization_id }}</td>
            <td class="py-2 font-mono">{{ execution.index_name }}</td>
            <td class="py-2">
              <div class="flex items-center gap-2">
                <component :is="statusIcon(execution)" class="w-4 h-4"/>
                <span>{{ statusLabel(execution.status) }}</span>
              </div>
            </td>
            <td class="py-2">
              <span v-if="execution.stats">{{ productStatsLabel(execution) }}</span>
              <span v-else>—</span>
              <a v-if="skippedCount(execution)"
                 href="#"
                 class="block text-xs text-primary"
                 :aria-expanded="String(expandedId === execution.id)"
                 @click.prevent="toggleSkipped(execution.id)"
              >{{ expandedId === execution.id ? 'Hide skipped' : 'Show skipped' }}</a>
            </td>
            <td class="py-2">
              <index-execution-last-update :index-execution="execution"/>
              <div class="text-xs text-gray-dark">{{ durationLabel(execution) }}</div>
            </td>
            <td class="py-2 text-red-600 max-w-xs">
              <!-- native title attribute, not vx-tooltip: vx-tooltip renders its `text` prop as raw
                   innerHTML, which would be an XSS vector for indexer-originated error messages -->
              <span v-if="execution.error" class="truncate block" :title="execution.error.message">{{ execution.error.message }}</span>
              <span v-else>—</span>
            </td>
          </tr>
          <!-- PRO-719: products the indexer skipped (e.g. too long to embed). Rendered as text, never as HTML:
               ids and names come straight from the customer's feed. -->
          <tr v-if="expandedId === execution.id" :key="`${execution.id}-skipped`" class="bg-gray-a10 border-solid border-0 border-b border-gray-border">
            <td colspan="6" class="py-2 px-4">
              <table class="w-full text-xs">
                <thead>
                  <tr class="text-left text-gray-dark">
                    <th class="py-1 w-48">Item ID</th>
                    <th class="py-1">Name</th>
                    <th class="py-1">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(skip, i) in execution.stats.skipped" :key="i">
                    <td class="py-1 font-mono truncate max-w-xs" :title="skip.id">{{ skip.id || '—' }}</td>
                    <td class="py-1">{{ skip.name || '—' }}</td>
                    <td class="py-1">{{ skipReason(skip) }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-if="skippedCount(execution) > execution.stats.skipped.length" class="text-xs text-gray-dark pt-1">
                Showing the first {{ execution.stats.skipped.length }} of {{ skippedCount(execution) }} skipped products.
              </div>
            </td>
          </tr>
        </template>
        <tr v-if="!sortedExecutions.length">
          <td colspan="6" class="py-4 text-gray">No indexations found.</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import moment from 'moment'
import adminIndexExecutionsService from '@/services/amio/admin-index-executions.service'
import IndexExecutionLastUpdate from '@/components/settings/indexes/IndexExecutionLastUpdate.vue'
import StatusSuccessIcon from '@/assets/images/icons/StatusSuccessIcon.vue'
import StatusErrorIcon from '@/assets/images/icons/StatusErrorIcon.vue'
import StatusWarningIcon from '@/assets/images/icons/StatusWarningIcon.vue'
import stringUtils from '@/utils/string.utils'
import { formatProductStats, formatSkipReason, getSkippedProductCount } from '@/utils/index-execution.utils'
import toaster from '@/utils/toaster'

const STATUSES = ['queued', 'created', 'running', 'finished', 'error', 'aborted', 'restarted']
const IN_PROGRESS_STATUSES = ['queued', 'created', 'running', 'restarted']
const POLL_INTERVAL_MS = 12000

export default {
  name: 'AdminIndexationOverview',

  components: { IndexExecutionLastUpdate, StatusSuccessIcon, StatusErrorIcon, StatusWarningIcon },

  data() {
    return {
      executions: [],
      filters: { orgId: '', status: 'all' },
      pollTimer: null,
      isDestroyed: false,
      now: moment(),
      requestSeq: 0,
      expandedId: null
    }
  },

  computed: {
    statusFilterOptions() {
      return ['all', ...STATUSES]
    },
    sortedExecutions() {
      const inProgress = this.executions.filter(execution => IN_PROGRESS_STATUSES.includes(execution.status))
      const settled = this.executions.filter(execution => !IN_PROGRESS_STATUSES.includes(execution.status))
      return [...inProgress, ...settled]
    }
  },

  methods: {
    onStatusChange(status) {
      this.filters.status = status
      this.reload()
    },

    async reload({ showToastOnError = true } = {}) {
      this.now = moment()
      // A poll tick and a filter-triggered reload can overlap; without this, whichever request
      // resolves last wins even if it was started for filters that no longer apply. Only the
      // most-recently-started request is allowed to write its result.
      const requestSeq = ++this.requestSeq
      try {
        const executions = await adminIndexExecutionsService.list({
          orgId: this.filters.orgId || null,
          status: this.filters.status !== 'all' ? this.filters.status : null
        })
        if (requestSeq !== this.requestSeq) return
        this.executions = executions
      } catch (err) {
        if (requestSeq !== this.requestSeq) return
        if (showToastOnError) toaster.error(err.message || err)
      }
    },

    statusIcon(execution) {
      // a finished run that had to skip products is a success with warnings (PRO-719)
      if (execution.status === 'finished') return this.skippedCount(execution) ? 'status-warning-icon' : 'status-success-icon'
      if (execution.status === 'error') return 'status-error-icon'
      return 'status-warning-icon'
    },

    skippedCount(execution) {
      return getSkippedProductCount(execution)
    },

    productStatsLabel(execution) {
      return formatProductStats(execution.stats)
    },

    skipReason(skip) {
      return formatSkipReason(skip)
    },

    toggleSkipped(executionId) {
      this.expandedId = this.expandedId === executionId ? null : executionId
    },

    statusLabel(status) {
      return stringUtils.capitalizeFirstChar(status)
    },

    durationLabel(execution) {
      const start = moment(execution.created_on)
      const end = execution.finished_on ? moment(execution.finished_on) : this.now
      return moment.duration(end.diff(start)).humanize()
    },

    startPolling() {
      if (this.pollTimer) return
      this.pollTimer = setInterval(() => {
        this.reload({ showToastOnError: false })
      }, POLL_INTERVAL_MS)
    },

    stopPolling() {
      if (!this.pollTimer) return
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  },

  async mounted() {
    await this.reload()
    if (this.isDestroyed) return
    this.startPolling()
  },

  beforeDestroy() {
    this.isDestroyed = true
    this.stopPolling()
  }
}
</script>
